import { useLocation, Link } from "react-router-dom";
import { useState } from "react";
import { APPIMAGES } from "../utils/global-variables-and-types";
import { useOrganization } from "../context/OrganizationContext";
import { CreateOrganizationModal } from "./CreateOrganizationModal";
import {
  LayoutDashboard,
  FolderKanban,
  FileText,
  Building2,
  ChevronDown,
  Plus,
  LucideIcon,
} from "lucide-react";

const NAV_ITEMS: {
  path: string;
  label: string;
  icon: LucideIcon;
  requiresOrg?: boolean;
}[] = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/projects", label: "Projects", icon: FolderKanban },
  { path: "/forms", label: "Forms", icon: FileText },
  {
    path: "/organization",
    label: "Organization",
    icon: Building2,
    requiresOrg: true,
  },
];

const CREATE_ORG_OPTION = "__create_organization__";

export const Sidebar = () => {
  const location = useLocation();
  const {
    organizations,
    selectedOrganizationId,
    setSelectedOrganizationId,
    setOrganizations,
  } = useOrganization();
  const [isCreateOrgOpen, setIsCreateOrgOpen] = useState(false);

  // Helper per la selezione dello stato attivo dei link
  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <aside className="w-72 h-full shrink-0 rounded-2xl bg-sidebar border border-border flex flex-col p-4">
      {/* Logo */}
      <Link
        to="/dashboard"
        className="group flex items-center gap-3 px-2 pt-2 pb-5 mb-6 border-b border-border"
      >
        <div className="size-10 shrink-0 rounded-xl bg-gradient-to-br from-primary to-primary-hover flex items-center justify-center shadow-[0_0_16px_-2px_var(--color-primary)] transition-all duration-200 group-hover:scale-105 group-hover:shadow-[0_0_22px_-2px_var(--color-primary)]">
          <img
            className="w-7 h-7 object-contain brightness-0 invert"
            src={APPIMAGES.logo}
            alt="FormsAware Logo"
          />
        </div>
        <p className="text-lg font-display font-bold tracking-tight">
          <span className="text-ink">Forms</span>
          <span className="text-primary">Aware</span>
        </p>
      </Link>

      {/* Organization selector */}
      <div className="flex flex-col w-full px-2 mb-6">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold tracking-wider text-ink-faint uppercase">
            Organization
          </p>
          <button
            type="button"
            onClick={() => setIsCreateOrgOpen(true)}
            aria-label="Create organization"
            title="Create organization"
            className="flex items-center justify-center size-5 rounded-md bg-surface-sunken text-ink-muted hover:bg-primary-tint hover:text-primary transition-colors"
          >
            <Plus size={12} strokeWidth={2.75} />
          </button>
        </div>
        <div className="relative">
          <select
            className="w-full appearance-none bg-surface border border-border rounded-xl py-2.5 pl-3 pr-9 text-sm font-medium text-ink shadow-xs outline-none focus:border-primary focus:ring-4 focus:ring-primary-tint transition-colors"
            value={selectedOrganizationId ?? ""}
            onChange={(e) => {
              if (e.target.value === CREATE_ORG_OPTION) {
                setIsCreateOrgOpen(true);
                return;
              }
              setSelectedOrganizationId(e.target.value);
            }}
          >
            {organizations.length === 0 ? (
              <option disabled value="">No organizations found</option>
            ) : (
              organizations.map((org) => (
                <option key={org._id} value={org._id}>
                  {org.name}
                </option>
              ))
            )}
            <option value={CREATE_ORG_OPTION}>+ New organization</option>
          </select>
          <ChevronDown
            size={15}
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint"
          />
        </div>
      </div>

      {/* Menu */}
      <div className="flex flex-col w-full px-2">
        <p className="text-[11px] font-semibold tracking-wider text-ink-faint mb-2 uppercase">
          Menu
        </p>

        <nav className="flex flex-col gap-1">
          {NAV_ITEMS.map(({ path, label, icon: Icon, requiresOrg }) => {
            const active = isActive(path);
            const disabled = requiresOrg && !selectedOrganizationId;

            return (
              <Link to={disabled ? "#" : path} key={path}>
                <button
                  disabled={disabled}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors duration-150 ${
                    active
                      ? "bg-primary-tint text-primary"
                      : "text-ink-muted hover:bg-surface-hover hover:text-ink"
                  } ${disabled ? "opacity-40 cursor-not-allowed hover:bg-transparent hover:text-ink-muted" : ""}`}
                >
                  <Icon size={18} strokeWidth={2} />
                  <span>{label}</span>
                </button>
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="mt-auto px-2 pt-4">
        <div className="rounded-xl bg-surface border border-border p-3 shadow-xs">
          <p className="text-xs font-semibold text-ink">Need help?</p>
          <p className="text-xs text-ink-muted mt-0.5">
            Docs &amp; support for FormsAware.
          </p>
        </div>
      </div>

      <CreateOrganizationModal
        isOpen={isCreateOrgOpen}
        onClose={() => setIsCreateOrgOpen(false)}
        onCreated={(organization) => {
          setOrganizations((prev) => [...prev, organization]);
          setSelectedOrganizationId(organization._id);
        }}
      />
    </aside>
  );
};
